import { useEffect, useMemo, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, Stack } from "@altimateai/lego";
import { getTeamConflictDetails, downloadTeamConflictDetailsCSV } from "@/helpers/apis";
import { DownloadTable } from "@/components/downloadTable";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { useParams, useSearchParams } from "react-router-dom";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import { useQuery } from "@tanstack/react-query";
import useConflictingTeamsTableHeaders from "./useConflictingTeamsTableHeaders";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";

interface ConflictingTeamsListModalContentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currency?: string;
}

const ConflictingTeamsListModalContent = ({
  open,
  onOpenChange,
  currency = "USD",
}: ConflictingTeamsListModalContentProps) => {
  const { id: groupId } = useParams();
  const { page, setPage, pageSize, setTotalPages, totalPages, totalItems, setTotalItems } =
    useTablePagination();
  const { isInitialized } = useDatatableContext();
  const prevOpenRef = useRef(open);
  const [searchParams] = useSearchParams();
  const { updateSearchParamsByKey } = useFiltersBySearchParams();

  // Sort state from URL params
  const sortKey = searchParams.get("sortAttribute") || "query_cost";
  const sortOrder = (searchParams.get("sortOrder") || "desc") as "asc" | "desc";

  // Build query params
  const getQueryParams = useCallback(() => {
    return {
      page: page + 1,
      size: pageSize,
      sort_key: sortKey,
      sort_order: sortOrder,
    };
  }, [page, pageSize, sortKey, sortOrder]);

  // Fetch data using useQuery
  const {
    data: conflicts,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["team-conflict-details", groupId, page, pageSize, sortKey, sortOrder],
    queryFn: () =>
      groupId ? getTeamConflictDetails(groupId, getQueryParams()) : Promise.resolve(null),
    enabled: open && !!groupId && isInitialized,
  });

  // Update pagination totals when data changes
  useEffect(() => {
    if (conflicts) {
      setTotalPages(Math.ceil(conflicts.total / pageSize));
      setTotalItems(conflicts.total);
    }
  }, [conflicts, pageSize, setTotalPages, setTotalItems]);

  // Reset pagination and sort state when modal closes
  useEffect(() => {
    if (prevOpenRef.current && !open) {
      updateSearchParamsByKey({
        sortAttribute: undefined,
        sortOrder: undefined,
        page: undefined,
      });
    }
    prevOpenRef.current = open;
  }, [open, updateSearchParamsByKey]);

  const handleDownload = useCallback(async () => {
    if (!groupId) return;

    const params: Record<string, string> = {
      sort_key: sortKey,
      sort_order: sortOrder,
    };

    try {
      await downloadTeamConflictDetailsCSV(groupId, params);
    } catch (err) {
      console.error("Failed to download team conflict details CSV:", err);
    }
  }, [groupId, sortKey, sortOrder]);

  const tableHeaders = useConflictingTeamsTableHeaders({
    currency,
    responseCurrency: conflicts?.currency,
  });

  const tableData = useMemo(() => {
    if (!conflicts) return { items: [] };
    return { items: conflicts.conflicts };
  }, [conflicts]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!al-max-w-6xl al-overflow-hidden">
        <DialogHeader className="al-flex-shrink-0">
          <DialogTitle>Teams with Ownership Conflicts</DialogTitle>
        </DialogHeader>
        <Stack direction="column" className="al-flex-1 al-min-h-0 al-overflow-hidden">
          <Stack direction="row" align="center" justify="end" className="al-mt-2 al-flex-shrink-0">
            <DownloadTable
              handleDownload={handleDownload}
              buttonProps={{
                disabled: isLoading || !conflicts?.conflicts?.length,
              }}
            />
          </Stack>
          <Stack direction={"column"} className={"al-min-h-80"}>
            {error && (
              <div className="al-rounded-md al-bg-destructive/10 al-border al-border-destructive/20 al-p-4">
                <p className="al-text-sm al-text-destructive">Failed to load conflict details</p>
              </div>
            )}

            {!error && (
              <>
                <Stack className={"al-max-h-[70vh]"}>
                  <DataTable
                    header={tableHeaders}
                    data={tableData}
                    isLoading={isLoading}
                    minWidth={900}
                  />
                </Stack>
                <Paginate
                  isLoading={isLoading}
                  itemCount={totalItems}
                  page={page}
                  numPages={totalPages}
                  pageSize={pageSize}
                  onPageClick={setPage}
                  onPageSizeChange={() => {}}
                  showPageSize={false}
                />
              </>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ConflictingTeamsListModalContent;
